<template>
  <div class="ap" role="dialog" aria-modal="true" :aria-label="title" data-test="action-panel">
    <!-- Phone only: the dimmed strip above the sheet. A tap on it closes (the X is the accessible way). -->
    <div class="ap__strip" aria-hidden="true" data-test="action-panel-strip" @click="$emit('close')"></div>

    <div class="ap__sheet">
      <span class="ap__grabber" aria-hidden="true"></span>

      <header class="ap__head">
        <button
          v-if="showBack"
          type="button"
          class="ap__icon-btn ap__back"
          aria-label="Back"
          data-test="action-panel-back"
          @click="$emit('back')"
        ><i class="fa fa-arrow-left" aria-hidden="true"></i></button>

        <div class="ap__titles">
          <div class="ap__title" data-test="action-panel-title">{{ title }}</div>
          <div v-if="subtitle" class="ap__sub" data-test="action-panel-sub">{{ subtitle }}</div>
        </div>

        <button
          type="button"
          class="ap__icon-btn"
          aria-label="Close"
          data-test="action-panel-close"
          @click="$emit('close')"
        ><i class="fa fa-times" aria-hidden="true"></i></button>
      </header>

      <div class="ap__body" data-test="action-panel-body">
        <slot />
      </div>

      <footer v-if="$scopedSlots.footer" class="ap__foot" data-test="action-panel-foot">
        <slot name="footer" />
      </footer>
    </div>
  </div>
</template>

<script>
/**
 * The frame every Patient Profile action panel sits in (design "Patient Action Panels", 10/9),
 * opened by PatientPanelActionHost.openActionPanel() through the host page's one <slideout-panel>:
 *
 *   header   optional back arrow · title · the patient-name line · X
 *   body     the default slot; it is the ONLY scroller (so the panel has no scrollbar gutter)
 *   footer   the `footer` slot, pinned: the one main button, saying exactly what it will do
 *
 * Presentational: no store, no mixin, no <slideout-panel> (the host page owns the only outlet).
 *
 * ⛔ CLOSING. The shell emits `close` (the X, and a tap on the phone's dimmed strip) and `back`; it
 * never emits `closePanel` itself. vue2-slideout-panel binds only `closePanel`, and only on the
 * component it renders — the panel — so each panel handles `@close` and decides: most forward it
 * with `this.$emit('closePanel', {})`; Collect Payment asks first while a charge is in flight. Escape
 * and a click on the scrim never reach a panel at all — the library closes the topmost slide-out
 * itself, unless the host's setCloseLocked(true) turned that off.
 *
 * Sizes: 480px wide on desktop and tablet (the host's $showPanel width; the library forces 100%
 * below it). A phone (<= $pp-phone-max) gets a full-height sheet with a dimmed strip on top, a
 * full-width main button and room for the home bar. Touch sizes live in _action-panel.scss.
 *
 * ⛔ No prop named `patNum`, `siteID`, `loading` or `allSettings` (mixin.js shadows them).
 */
export default {
  name: 'ActionPanelShell',

  props: {
    title: { type: String, required: true },
    /** The patient-name line under the title (already masked at the data boundary when the demo mask is on). */
    subtitle: { type: String, default: '' },
    /** Draw the back arrow (a later step of a multi-step panel). */
    showBack: { type: Boolean, default: false },
  },
};
</script>

<style lang="scss" scoped>
@import './action-panel';

// Exactly the slide-out's height (the library's `.slideout` is fixed, top 0 / bottom 0) with the BODY
// as the scroller — TaskDrawer.vue `.td`. A panel that scrolled itself left a ~15px scrollbar gutter.
.ap {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: $pp-surface;
  border-left: 1px solid $pp-line;
  color: $pp-ink;
  font-size: 14px;
}

.ap__strip { display: none; }

.ap__sheet {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: $pp-surface;
}

.ap__grabber { display: none; }

.ap__head {
  flex: none;
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 16px 14px 14px 20px;
  border-bottom: 1px solid $pp-line;
}

.ap__titles {
  flex: 1 1 auto;
  min-width: 0;
  padding-top: 2px;
}

.ap__title {
  font-family: $pp-font;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: $pp-ink;
}

.ap__sub {
  margin-top: 3px;
  font-size: 13px;
  color: $pp-ink-3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ap__icon-btn {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: $pp-radius;
  background: transparent;
  color: $pp-ink-3;
  font-family: inherit;
  font-size: 16px;
  cursor: pointer;

  &:hover { background: $pp-bg; color: $pp-ink; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: 1px; }

  @include ap-touch {
    width: 44px;
    height: 44px;
  }
}

.ap__back { margin: 0 2px 0 -8px; }

.ap__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
}

.ap__foot {
  flex: none;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 20px 14px 24px;
  border-top: 1px solid $pp-line;
  background: $pp-surface;
}

// Footer content hooks (the panel's own markup, hence ::v-deep): a one-line note on the left, and the
// main button — which fills the footer on a phone.
.ap__foot ::v-deep .ap-foot-note {
  flex: 1 1 0;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  color: $pp-ink-2;
}

// Phone — the design's 3a sheet: a dimmed strip on top, the sheet below it with rounded top corners.
@include ap-phone {
  .ap {
    border-left: 0;
    background: transparent;
  }

  .ap__strip {
    display: block;
    flex: none;
    // Tall enough for a thumb; clear of the notch when the page is edge to edge.
    height: calc(44px + env(safe-area-inset-top, 0px));
  }

  .ap__sheet {
    border-radius: 14px 14px 0 0;
    overflow: hidden;
    box-shadow: 0 -8px 30px rgba(12, 15, 14, 0.18);
  }

  .ap__grabber {
    display: block;
    flex: none;
    width: 36px;
    height: 5px;
    margin: 7px auto 0;
    border-radius: 3px;
    background: $neutral-300;
  }

  .ap__head { padding: 4px 8px 12px 16px; }

  // Clear of the home bar (the design's 30px; env() is 0 in a browser tab, which has its own bar).
  .ap__foot { padding: 12px 16px calc(16px + env(safe-area-inset-bottom, 0px)); }

  .ap__foot ::v-deep .ap-foot-note { flex-basis: 100%; }

  .ap__foot ::v-deep .ap-foot-main {
    flex: 1 1 0;
    width: 100%;
  }
}
</style>

<style lang="scss">
@import './action-panel';

// Global, and only for the action panels (the library renders the `.slideout` outside this
// component's scope; the host opens them with cssClass 'ap-slideout').

// The design's lighter backdrop behind THIS panel only — the same rule as the Task settings panel
// (TaskSettingsPanel.vue 'tsp-slideout'). Browsers without :has() keep the library's darker one.
.slideout-panel:has(.ap-slideout) .slideout-panel-bg {
  background-color: rgba(12, 15, 14, 0.18);
}

@include ap-phone {
  // The sheet owns its own background, so the dimmed strip above it shows the scrim through.
  .slideout-panel .slideout-wrapper .slideout.ap-slideout {
    background: transparent;
    overflow: hidden;
  }

  // It slides UP on a phone, not in from the right (the library's translateX rules, outranked).
  .slideout-panel .slideout-wrapper .slideout.open-on-right.ap-slideout {
    &.slideIn-enter,
    &.slideIn-leave-to { transform: translateY(100%); }

    &.slideIn-enter-to,
    &.slideIn-leave { transform: translateY(0); }
  }

  // The AI agent's floating button (z 9999) sits over the sheet's footer button. Hide it while an
  // action panel is open; it returns when the panel closes. Same pattern as agent-panel.css
  // (the follow-up drawer's `fu-drawer-panel`, the app shell's phone drawer).
  body:has(.slideout-panel .ap-slideout) .copilotKitSidebar.ac-root {
    display: none !important;
  }
}
</style>
